export const HTTP_FORBIDDEN = 403;
export const HTTP_NOT_FOUND = 404;
export const HTTP_SERVER_ERROR_MIN = 500;

export const NO_ACCESS = "no_access" as const;
export const NOT_FOUND = "not_found" as const;
export const SERVER_ERROR = "server_error" as const;

export const RESOURCE_GET_ERROR_STATUSES = [
  NO_ACCESS,
  NOT_FOUND,
  SERVER_ERROR,
] as const;
export type ResourceGetErrorStatus =
  (typeof RESOURCE_GET_ERROR_STATUSES)[number];

export type ResourcePermissionStatus =
  | ResourceGetErrorStatus
  | "not_allowed"
  | "ok";

function isResourceGetErrorStatus(
  value: unknown,
): value is ResourceGetErrorStatus {
  return (
    typeof value === "string" &&
    RESOURCE_GET_ERROR_STATUSES.some((status) => status === value)
  );
}

export function httpCodeToErrorStatus(
  code: number | undefined,
): ResourceGetErrorStatus {
  if (code === HTTP_FORBIDDEN) return NO_ACCESS;
  if (code === HTTP_NOT_FOUND) return NOT_FOUND;
  if (code && code >= HTTP_SERVER_ERROR_MIN) return SERVER_ERROR;
  return SERVER_ERROR;
}

export function httpCodeToPermissionStatus(
  code: number | undefined,
): "not_allowed" | "server_error" | undefined {
  if (code === HTTP_FORBIDDEN) return "not_allowed";
  if (code && code >= HTTP_SERVER_ERROR_MIN) return SERVER_ERROR;
  return undefined;
}

// Parses an RTK Query error into a ResourceGetErrorStatus, handling both the
// CUSTOM_ERROR shape (thrown by baseQuery for pre-classified errors) and
// plain numeric HTTP status codes.
export function getResourceErrorStatus(error: unknown): ResourceGetErrorStatus {
  if (typeof error !== "object" || error === null || !("status" in error)) {
    return httpCodeToErrorStatus(undefined);
  }
  const { status } = error as { status?: unknown };
  if (status === "CUSTOM_ERROR" && "error" in error) {
    const payload = (error as { error: unknown }).error;
    if (isResourceGetErrorStatus(payload)) {
      return payload;
    }
    return httpCodeToErrorStatus(undefined);
  }
  if (typeof status === "number") {
    return httpCodeToErrorStatus(status);
  }
  return httpCodeToErrorStatus(undefined);
}
